import dayjs from 'dayjs';

import {
  createShortcutGroups,
  createShortcuts,
  createShortcutTemplates,
} from './seed/applications';
import { createBookings } from './seed/bookings';
import {
  createCategories,
  createCombos,
  createGoods,
  createServices,
  createTariffs,
} from './seed/catalog';
import { createEventHistory, createPaymentHistory } from './seed/history';
import { createHostMapLayout } from './seed/hostMap';
import {
  applySeedHostApplications,
  createHostGroups,
  createHosts,
  createSessions,
} from './seed/hosts';
import {
  SandboxClient,
  SandboxClubReview,
  SandboxCompanyNews,
  SandboxSetting,
  SandboxState,
  SandboxTask,
  SandboxWorker,
} from './types';

const clientNames = [
  ['alex', 'Алексей', 'Смирнов', 1250],
  ['nightowl', 'Мария', 'Соколова', 420],
  ['cyberfox', 'Илья', 'Кузнецов', 2750],
  ['pixel', 'Анна', 'Попова', 90],
  ['storm', 'Денис', 'Волков', 810],
  ['neo', 'Олег', 'Морозов', 1500],
  ['vector', 'Екатерина', 'Лебедева', 630],
  ['raiden', 'Максим', 'Фёдоров', 980],
] as const;

const SANDBOX_SCREENSAVER_IMAGE =
  'https://storage.yandexcloud.net/smartshell-static/static/sl_promo.png';

const createClient = (
  source: typeof clientNames[number],
  index: number,
  anchorMs: number
): SandboxClient => ({
  __typename: 'User',
  origin: 'preset',
  id: 100 + index,
  uuid: `preset:client:${index + 1}`,
  nickname: source[0],
  phone: `+7999000000${index + 1}`,
  phone_suffix: `00000${index + 1}`,
  first_name: source[1],
  middle_name: '',
  last_name: source[2],
  dob: `199${index}-0${(index % 8) + 1}-1${index}`,
  deposit: source[3],
  bonus: index * 35,
  last_client_activity: new Date(anchorMs - index * 3600000).toISOString(),
  last_worker_activity: '',
  last_comment:
    index === 0
      ? { text: 'Постоянный клиент тестового клуба', is_important: true }
      : null,
  group: {
    uuid: index < 2 ? 'preset:group:vip' : 'preset:group:regular',
    title: index < 2 ? 'VIP' : 'Основная',
    discount: index < 2 ? 10 : 0,
  },
  creator: null,
  created_at: new Date(anchorMs - (index + 30) * 86400000).toISOString(),
  updated_at: new Date(anchorMs).toISOString(),
  disabled_at: '',
  banned_at: null,
  total_hours: 12 + index * 9,
  roles: [{ id: 2, alias: 'client', title: 'Клиент' }],
  user_discount: index < 2 ? 5 : 0,
  discounts: [],
  role_ids: [2],
  permissions: [],
  avatar_url: '',
  unverified: false,
  pausable_info: null,
});

const createSettings = (anchorMs: number): SandboxSetting[] =>
  [
    ['shell.club_name', 'Тестовый клуб'],
    ['backend.currency', 'RUB'],
    ['backend.country_code', 'RU'],
    ['backend.timezone', '3'],
    ['backend.telegram.channel_id', '{"id":"","type":"PRIVATE","title":""}'],
    ['cashbox.enabled', '0'],
    ['shell.bonus_system.deposit_refill_enabled', '1'],
    ['backend.refunds.enabled', '1'],
    ['backend.post_payment.enabled', '1'],
    ['backend.post_payment.max_duration', '10800'],
    ['backend.booking.online.min_duration', '3600'],
    ['backend.booking.online.max_duration', '86400'],
    ['backend.host_map.layout', JSON.stringify(createHostMapLayout())],
    ['shell.skin.accent_color', '#7C62FF'],
    ['shell.skin.additional_color', '#000000'],
    ['shell.skin.background_color', '#232323'],
    [
      'shell.skin.desktop.image',
      'http://static.smartshell.gg/backgrounds_default/1.png',
    ],
    ['shell.skin.logo', ''],
    ['shell.skin.idle.image', SANDBOX_SCREENSAVER_IMAGE],
    ['backend.skin.smart_kiosk.image', SANDBOX_SCREENSAVER_IMAGE],
    ['shell.skin.smart_tv.image', SANDBOX_SCREENSAVER_IMAGE],
  ].map(([alias, value], index) => ({
    id: index + 1,
    alias,
    value,
    updated_at: new Date(anchorMs).toISOString(),
    disabled: false,
  }));

const rolePermissions = [
  { id: 1, alias: 'worker_manage' },
  { id: 2, alias: 'role_browse' },
  { id: 3, alias: 'event_browse' },
  { id: 4, alias: 'payment_create' },
];

const workerFixtures = [
  ['owner', 'Владелец', 'Аврора', 'Администратор'],
  ['manager', 'Менеджер', 'Борис', 'Орлов'],
  ['operator', 'Оператор', 'Светлана', 'Миронова'],
  ['operator', 'Оператор', 'Тимур', 'Беляев'],
] as const;

const createSeedWorkers = (anchorMs: number): SandboxWorker[] => {
  const ownerReference = {
    id: 1,
    uuid: 'preset:worker:owner',
    nickname: 'Аврора',
    first_name: 'Аврора',
    last_name: 'Администратор',
  };
  return workerFixtures.map(([alias, title, firstName, lastName], index) => ({
    __typename: 'User',
    id: index + 1,
    uuid: index === 0 ? 'preset:worker:owner' : `preset:worker:${index + 1}`,
    login: index === 0 ? 'sandbox' : `worker${index + 1}`,
    nickname: firstName,
    phone: `+7999111000${index}`,
    phone_suffix: `11000${index}`,
    first_name: firstName,
    middle_name: '',
    last_name: lastName,
    dob: `199${index}-01-01`,
    bonus: 0,
    last_client_activity: '',
    last_worker_activity: new Date(anchorMs - index * 3600000).toISOString(),
    group: null,
    creator: index === 0 ? null : ownerReference,
    created_at: new Date(anchorMs - (index + 10) * 86400000).toISOString(),
    updated_at: new Date(anchorMs).toISOString(),
    disabled_at: '',
    banned_at: '',
    total_hours: 0,
    roles: [
      {
        id: index === 0 ? 1 : alias === 'manager' ? 2 : 3,
        alias,
        title,
        permissions: rolePermissions,
      },
    ],
    user_discount: 0,
    discounts: [] as [],
    role_ids: [index === 0 ? 1 : alias === 'manager' ? 2 : 3],
    permissions: rolePermissions,
    avatar_url: '',
    unverified: false,
    is_owner: index === 0,
  }));
};

const createTasks = (
  worker: SandboxWorker,
  anchorMs: number
): SandboxTask[] => [
  {
    __typename: 'WorkerTask',
    origin: 'preset',
    id: 1,
    title: 'Протереть столы',
    assigned_to: null,
    deadline_at: null,
    finished_at: null,
    created_at: new Date(anchorMs - 2 * 3600000).toISOString(),
    updated_at: new Date(anchorMs - 2 * 3600000).toISOString(),
  },
  {
    __typename: 'WorkerTask',
    origin: 'preset',
    id: 2,
    title: 'Принять поставку товара',
    assigned_to: worker,
    deadline_at: dayjs(anchorMs).endOf('day').toISOString(),
    finished_at: null,
    created_at: new Date(anchorMs - 3600000).toISOString(),
    updated_at: new Date(anchorMs - 3600000).toISOString(),
  },
  {
    __typename: 'WorkerTask',
    origin: 'preset',
    id: 3,
    title: 'Обновить игры в клубе',
    assigned_to: worker,
    deadline_at: dayjs(anchorMs).subtract(1, 'day').endOf('day').toISOString(),
    finished_at: new Date(anchorMs - 3 * 3600000).toISOString(),
    created_at: new Date(anchorMs - 2 * 86400000).toISOString(),
    updated_at: new Date(anchorMs - 3 * 3600000).toISOString(),
  },
];

const createClubReviews = (
  clients: SandboxClient[],
  anchorMs: number
): SandboxClubReview[] =>
  Array.from({ length: 8 }, (_, index) => {
    const grade = 5 - (index % 5);
    const textByGrade: Record<number, string> = {
      5: 'Отличная атмосфера, мощные компьютеры и вежливый администратор.',
      4: 'Хороший клуб, всё понравилось. Иногда приходится ждать свободное место.',
      3: 'В целом играть можно, но гарнитура была уставшая, а заказы несли долго.',
      2: 'Пришлось долго ждать администратора, компьютер зависал, в зале было душно.',
      1: 'Очень разочарован: место было грязным, техника подвисала, проблему не решили.',
    };

    return {
      __typename: 'ClubComment',
      id: 200 + index,
      text: textByGrade[grade],
      author: {
        uuid: clients[index % clients.length].uuid,
        nickname: clients[index % clients.length].nickname,
        phone: clients[index % clients.length].phone,
      },
      created_at: new Date(anchorMs - index * 86400000).toISOString(),
      host_alias: `PC-${String((index % 8) + 1).padStart(2, '0')}`,
      host_group_name: index % 3 === 0 ? 'VIP-зона' : 'Основной зал',
      grade,
      is_read: index % 3 === 0,
      request_contact: index === 4,
      request_contact_info: index === 4 ? 'Просьба уточнить детали визита' : '',
      tips: index % 2 === 0 ? 100 + index * 10 : null,
      worker_name: index % 2 === 0 ? 'Аврора Администратор' : null,
    };
  });

const createCompanyNews = (anchorMs: number): SandboxCompanyNews[] => {
  const publicationEndDates: Array<string | null> = [
    dayjs(anchorMs).add(3, 'day').toISOString(),
    dayjs(anchorMs).add(1, 'month').toISOString(),
    null,
    dayjs(anchorMs).add(6, 'day').toISOString(),
  ];
  const news = [
    {
      active: true,
      title: 'Ночной турнир по Counter-Strike 2',
      content:
        '<p>В эту субботу собираемся на ночной турнир 5×5. Начало регистрации в 21:00, первые матчи — в 22:00. Собирай команду или приходи один: свободных игроков распределим на месте.</p><p>Участие — 700 ₽ с человека, игровое время и напиток включены. Победителям достанутся часы в клубе и памятные призы.</p>',
      image: '/images/news/cs2-tournament.png',
      link: { url: '#/news', label: 'Записаться' },
    },
    {
      active: true,
      title: 'Летний ночной тариф уже доступен',
      content:
        '<p>Запустили ночной пакет: играй с 23:00 до 08:00 по фиксированной цене. В основном зале пакет стоит 990 ₽, в VIP-зоне — 1 490 ₽.</p><p>Тариф действует с воскресенья по четверг. Забронировать место можно заранее у администратора клуба.</p>',
      image: '/images/news/night-rate.png',
      link: { url: '#/news', label: 'Выбрать место' },
    },
    {
      active: true,
      title: 'Обновили компьютеры в VIP-зоне',
      content:
        '<p>В VIP-зоне теперь новые видеокарты, мониторы 240 Гц и механические клавиатуры. Заодно обновили кресла и добавили больше места для вещей.</p><p>Все популярные игры уже установлены, а настройки аккаунтов сохраняются между сеансами.</p>',
      image: '/images/news/vip-upgrade.png',
      link: { url: '#/news', label: 'Забронировать' },
    },
    {
      active: true,
      title: 'День рождения клуба — подарки весь день',
      content:
        '<p>В пятницу отмечаем день рождения клуба. Каждому гостю начислим бонусный час, а с 18:00 проведём розыгрыш пакетов игрового времени и клубного мерча.</p><p>Заходи с друзьями: для компаний от пяти человек подготовили отдельный подарок.</p>',
      image: '/images/news/club-birthday.png',
      link: { url: '#/news', label: 'Подробнее' },
    },
    {
      active: false,
      title: 'Профилактика оборудования в основном зале',
      content:
        '<p>Во вторник с 08:00 до 11:00 основной зал будет закрыт на профилактику. VIP-зона продолжит работать в обычном режиме.</p>',
      image: '',
      link: null,
    },
    {
      active: false,
      title: 'Открываем регистрацию в летнюю лигу Valorant',
      content:
        '<p>Готовим серию еженедельных матчей для любительских команд. Расписание, формат и призовой фонд опубликуем после завершения регистрации.</p>',
      image: '',
      link: { url: '#/news', label: 'Узнать первым' },
    },
  ];

  return news.map((item, index) => ({
    id: `sandbox-news-${index + 1}`,
    ...item,
    created_at: new Date(anchorMs - (index + 1) * 86400000).toISOString(),
    publication_period: {
      from: new Date(anchorMs - (index + 1) * 86400000).toISOString(),
      until: publicationEndDates[index] ?? null,
    },
    updated_at: new Date(anchorMs - index * 3600000).toISOString(),
  }));
};

export const createSeedState = (anchorMs: number): SandboxState => {
  const workers = createSeedWorkers(anchorMs);
  const worker = workers[0];
  const clients = clientNames.map((client, index) =>
    createClient(client, index, anchorMs)
  );
  const hostGroups = createHostGroups();
  const hosts = createHosts();
  const tariffs = createTariffs();
  const sessions = createSessions(anchorMs, tariffs);
  const hostsWithApplications = applySeedHostApplications(hosts, sessions);
  const goodCategories = createCategories();
  const goods = createGoods(goodCategories);
  const services = createServices();
  const combos = createCombos(tariffs, goods, services);
  const payments = createPaymentHistory(clients, goods, anchorMs);
  const shortcutGroups = createShortcutGroups();
  const shortcuts = createShortcuts();

  return {
    worker,
    workers,
    clients,
    hostGroups,
    hosts: hostsWithApplications,
    sessions,
    bookings: createBookings(clients, hostGroups, anchorMs),
    tasks: createTasks(worker, anchorMs),
    tariffs,
    goodCategories,
    goods,
    services,
    combos,
    payments,
    events: createEventHistory(clients, payments, workers, anchorMs),
    clubReviews: createClubReviews(clients, anchorMs),
    companyNews: createCompanyNews(anchorMs),
    settings: createSettings(anchorMs),
    shortcutGroups,
    shortcuts,
    shortcutTemplates: createShortcutTemplates(),
    nextPaymentId: 1000,
    nextBookingId: 1000,
    nextSessionId: 1000,
    nextCommandId: 1000,
    nextTaskId: 4,
    nextShortcutId: shortcuts.length + 1,
    nextShortcutGroupId: shortcutGroups.length + 1,
    nextEventOffset: 0,
  };
};
